/**
 * ParlaySummaryCard
 * Renders parlay analysis summaries and generated picks with headshots,
 * team badges, confidence indicators, and track-parlay buttons.
 */

import { useToast } from '@contexts/ToastContext';
import { buildTrackMessage, chatCardTrackRequest } from '@parlay-picker/shared/chatDataCards';
import type {
  AnalyzedLeg,
  GeneratedLegCard,
  GeneratedParlayCard,
  ParlaySummaryCardData,
} from '@parlay-picker/shared/chatTypes';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import { leagueKeyOrNull } from '@parlay-picker/shared/league';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { formatLegMatchup } from '@parlay-picker/shared/teamParsing';
import { findTeam, getTeamAbbr } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import { Bookmark, Check, Loader2, Shield, Star } from 'lucide-react';
import type { ReactElement } from 'react';
import { useCallback, useContext, useState } from 'react';
import { ChatTrackContext } from '../chatTrackContext';
import {
  AvatarWithBadge,
  confidenceDot,
  confidenceStyles,
  ExpandableText,
  VerdictBadge,
} from './cardHelpers';

export function ParlaySummaryCard({
  data,
  onSendMessage,
}: {
  data: ParlaySummaryCardData;
  onSendMessage?: (message: string) => void;
}): ReactElement {
  // Generated picks: data.picks is an array of GeneratedParlay objects
  const picks = data.picks;
  if (picks && picks.length > 0) {
    return (
      <GeneratedPicksDisplay
        picks={picks}
        generationId={data.generationId}
        quotaRemaining={data.quotaRemaining}
        onSendMessage={onSendMessage}
      />
    );
  }

  // Analysis result: data.legs is an array of leg analyses
  const legs = data.legs;
  const overallVerdict = data.overallVerdict || '';
  const legCount = data.legCount || legs?.length || 0;
  const strongCount = data.strongCount || 0;
  const estimatedTotalOdds = data.estimatedTotalOdds || '';

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-theme-primary" />
          <span className="text-sm font-semibold text-theme-text">Parlay Analysis</span>
        </div>
        <div className="flex items-center gap-2">
          {estimatedTotalOdds && (
            <span className="text-xs font-medium text-theme-text-secondary">
              {estimatedTotalOdds}
            </span>
          )}
          {overallVerdict && <VerdictBadge verdict={overallVerdict} />}
        </div>
      </div>
      {legCount > 0 && (
        <div className="text-xs text-theme-text-secondary">
          {strongCount}/{legCount} legs favorable
        </div>
      )}
      {legs && legs.length > 0 && (
        <div className="space-y-0">
          {legs.map((leg, i) => (
            <AnalysisSummaryLegRow key={i} leg={leg} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AnalysisSummaryLegRow({ leg }: { leg: AnalyzedLeg }): ReactElement {
  const playerName = leg.playerName || 'Unknown';
  const teamAbbr = leg.teamAbbr || '';
  const legLeagueKey = leagueKeyOrNull(leg.leagueKey);
  const headshotUrl = usePlayerHeadshotUrl(playerName, legLeagueKey);
  const teamInfo = findTeam(teamAbbr, legLeagueKey);

  return (
    <div className="flex gap-2 py-1.5 border-t border-theme-border/30 first:border-0 first:pt-0">
      <AvatarWithBadge headshotUrl={headshotUrl} teamLogo={teamInfo?.logo} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-theme-text truncate">{playerName}</span>
          {leg.verdict != null && <VerdictBadge verdict={String(leg.verdict)} />}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-theme-text-secondary">
            {String(leg.overUnder || '')} {leg.line != null ? String(leg.line) : ''}{' '}
            {formatStatLabel(String(leg.statType || ''))}
          </span>
          <div className="flex items-center gap-1.5 text-[10px]">
            {leg.seasonAverage != null && (
              <span className="text-theme-text-tertiary">avg {String(leg.seasonAverage)}</span>
            )}
            {leg.edge != null && (
              <span className={Number(leg.edge) > 0 ? 'text-theme-success' : 'text-theme-danger'}>
                {Number(leg.edge) > 0 ? '+' : ''}
                {String(leg.edge)}
              </span>
            )}
            {leg.odds != null && (
              <span className="text-theme-text-tertiary">{String(leg.odds)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Generated Picks Display (rich version with headshots + team badges)
// =============================================================================

export function GeneratedPicksDisplay({
  picks,
  generationId,
  quotaRemaining,
  onSendMessage,
}: {
  picks: GeneratedParlayCard[];
  generationId?: string;
  quotaRemaining?: number;
  onSendMessage?: (message: string) => void;
}): ReactElement {
  // Per-parlay tracking state: 'idle' | 'tracking' | 'tracked'
  const [trackState, setTrackState] = useState<Record<number, 'tracking' | 'tracked'>>({});
  const trackParlay = useContext(ChatTrackContext);
  const { showToast } = useToast();

  const handleTrack = useCallback(
    async (index: number, parlay: GeneratedParlayCard) => {
      if (trackState[index]) return;
      const request = trackParlay ? chatCardTrackRequest(parlay, generationId) : null;
      if (trackParlay && request) {
        setTrackState((prev) => ({ ...prev, [index]: 'tracking' }));
        const result = await trackParlay(request);
        if (result.success) {
          setTrackState((prev) => ({ ...prev, [index]: 'tracked' }));
        } else {
          setTrackState(({ [index]: _dropped, ...rest }) => rest);
          showToast(result.error ?? 'Failed to track parlay', 'error');
        }
        return;
      }
      // A card saved before picks carried ids: chat rebuilds the parlay from text
      if (!onSendMessage) return;
      setTrackState((prev) => ({ ...prev, [index]: 'tracking' }));
      onSendMessage(buildTrackMessage(parlay.legs || []));
      // Mark as tracked after a short delay (chat response confirms)
      setTimeout(() => {
        setTrackState((prev) => ({ ...prev, [index]: 'tracked' }));
      }, 3000);
    },
    [onSendMessage, trackParlay, generationId, trackState, showToast],
  );

  return (
    <div className="space-y-2">
      {picks.map((parlay, i) => {
        const legs = parlay.legs || [];
        const name = parlay.name || `Parlay ${i + 1}`;
        const totalOdds = parlay.totalOdds || parlay.odds || '';
        const confidence = parlay.confidence || '';
        const confStyle = confidenceStyles[confidence] || 'bg-theme-primary/10 text-theme-primary';
        const confDot = confidenceDot[confidence] || 'bg-theme-primary';
        const state = trackState[i];

        return (
          <div
            key={i}
            className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-theme-success" />
                <span className="text-sm font-semibold text-theme-text">{name}</span>
              </div>
              <div className="flex items-center gap-2">
                {confidence && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${confStyle}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${confDot}`} />
                    {confidence}
                  </span>
                )}
                {totalOdds && (
                  <span className="text-xs font-medium text-theme-text-secondary">{totalOdds}</span>
                )}
              </div>
            </div>
            {/* Reasoning */}
            {parlay.reasoning ? (
              <ExpandableText
                text={String(parlay.reasoning)}
                className="text-[10px] text-theme-text-secondary italic mt-0.5"
                lineClamp={2}
              />
            ) : null}
            {/* Legs */}
            <div className="space-y-1.5">
              {legs.map((leg, j) => (
                <ChatLegRow key={j} leg={leg} />
              ))}
            </div>
            {/* Track button */}
            {(onSendMessage || trackParlay) && legs.length > 0 && (
              <button
                type="button"
                disabled={!!state}
                onClick={() => handleTrack(i, parlay)}
                className={`flex items-center gap-1.5 text-[11px] font-medium transition-colors pt-1 border-t border-theme-border/50 ${
                  state === 'tracked'
                    ? 'text-theme-success cursor-default'
                    : state === 'tracking'
                      ? 'text-theme-text-tertiary cursor-wait'
                      : 'text-theme-primary hover:text-theme-primary-hover'
                }`}
              >
                {state === 'tracked' ? (
                  <Check className="w-3.5 h-3.5" />
                ) : state === 'tracking' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Bookmark className="w-3.5 h-3.5" />
                )}
                {state === 'tracked'
                  ? 'Tracked'
                  : state === 'tracking'
                    ? 'Tracking...'
                    : 'Track Parlay'}
              </button>
            )}
          </div>
        );
      })}
      {quotaRemaining != null && (
        <div className="text-[10px] text-theme-text-tertiary text-center">
          {quotaRemaining} generation{quotaRemaining !== 1 ? 's' : ''} remaining today
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Chat Leg Row (rich card with headshot, team badge, matchup, explanation)
// =============================================================================

export function ChatLegRow({ leg }: { leg: GeneratedLegCard }): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const playerName = leg.player || null;
  const teamName = leg.team || '';
  const opponentTeam = leg.opponentTeam || '';
  const position = leg.position || null;
  const statType = leg.statType || leg.prop || '';
  const isGameLevel = isTeamBet(leg.propType || leg.marketKey || statType);

  const legLeagueKey = leagueKeyOrNull(leg.leagueKey);
  const headshotUrl = usePlayerHeadshotUrl(isGameLevel ? null : playerName, legLeagueKey);
  const teamInfo = findTeam(teamName, legLeagueKey);
  const opponentInfo = findTeam(opponentTeam, legLeagueKey);
  const teamAbbr = teamInfo?.abbr || getTeamAbbr(teamName, legLeagueKey);
  const opponentAbbr =
    opponentInfo?.abbr || (opponentTeam ? getTeamAbbr(opponentTeam, legLeagueKey) : '');

  const explanation = leg.explanation?.summary ? String(leg.explanation.summary) : null;
  const overUnder = leg.overUnder || '';
  const line = leg.line != null ? String(leg.line) : '';
  const odds = leg.odds || '';

  // For game-level props, use team logo as avatar
  const avatarUrl = isGameLevel ? teamInfo?.logo || null : headshotUrl;
  const matchup = formatLegMatchup({
    team: teamAbbr,
    opponentTeam: opponentAbbr,
    isHomeGame: leg.isHomeGame,
  });
  const displayName = isGameLevel ? matchup || teamName || '?' : playerName || teamName || '?';

  return (
    <div className="flex gap-2.5 py-1 border-t border-theme-border/30 first:border-0 first:pt-0">
      <AvatarWithBadge
        headshotUrl={avatarUrl}
        teamLogo={!isGameLevel ? teamInfo?.logo : undefined}
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Name + prop */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-medium text-theme-text truncate">{displayName}</span>
            {!isGameLevel && position && (
              <span className="text-[10px] text-theme-text-tertiary shrink-0">{position}</span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs font-medium text-theme-text">
              {overUnder} {line}
            </span>
          </div>
        </div>

        {/* Row 2: Matchup + stat + odds */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-theme-text-tertiary">
            {teamAbbr && opponentAbbr ? `${teamAbbr} vs ${opponentAbbr}` : teamAbbr ? teamAbbr : ''}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-theme-text-secondary">
              {formatStatLabel(statType)}
            </span>
            {odds && <span className="text-[10px] text-theme-text-tertiary">{odds}</span>}
          </div>
        </div>

        {/* Row 3: AI explanation (optional) */}
        {explanation && (
          <p
            className={`text-[10px] text-theme-text-secondary italic mt-0.5 cursor-pointer ${expanded ? '' : 'line-clamp-1'}`}
            onClick={() => setExpanded(!expanded)}
            title="Click to expand"
          >
            {explanation}
          </p>
        )}
      </div>
    </div>
  );
}
